import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselIndicator,
} from 'src/shared/components/ui/carousel'
import { useState, useEffect } from 'react'

import { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import { DesignImage } from 'src/shared/components/Design'
import { Design_ } from 'src/api/db/apiDesigns'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { useOrgsApi } from 'src/api/db/apiOrgs'

export const ProductImageCarousel = ({
  chosen_product_variant_color_,
  selectedIllustration,
  setSelectedIllustration,
  product_id,
}: {
  chosen_product_variant_color_: ProductVariantColor_
  selectedIllustration?: any
  setSelectedIllustration?: (props: any) => void
  product_id?: number
}) => {
  const org_id = useGetOrgId()
  const { organizationById } = useOrgsApi({
    org_id,
  })
  const organization = organizationById?.data
  const chosen_logo_url = organization?.chosen_logo_url

  // Props kept for backward compatibility but not used with Carousel
  void selectedIllustration
  void setSelectedIllustration

  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(new Set())
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  let illustrations = chosen_product_variant_color_?.product_image ?? []
  illustrations = illustrations.sort(
    (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
  )

  // Filter out illustrations with failed image URLs or missing URLs
  const validIllustrations = illustrations.filter(
    (illustration) =>
      illustration?.image_url && !failedImageUrls.has(illustration.image_url)
  )

  // Validate images on mount and when illustrations change
  useEffect(() => {
    const validateImages = async () => {
      const newFailedUrls = new Set<string>()
      const imageUrls = illustrations
        .map((i) => i?.image_url)
        .filter((url): url is string => Boolean(url))

      await Promise.all(
        imageUrls.map(async (imageUrl) => {
          return new Promise<void>((resolve) => {
            const img = new Image()
            img.onload = () => resolve()
            img.onerror = () => {
              newFailedUrls.add(imageUrl)
              resolve()
            }
            img.src = imageUrl
            // Timeout after 5 seconds
            setTimeout(() => {
              if (!img.complete) {
                newFailedUrls.add(imageUrl)
              }
              resolve()
            }, 5000)
          })
        })
      )

      setFailedImageUrls(newFailedUrls)
    }

    validateImages()
  }, [
    illustrations
      .map((i) => i?.id)
      .filter(Boolean)
      .join(','),
    illustrations
      .map((i) => i?.image_url)
      .filter(Boolean)
      .join(','),
  ])

  const handleCarouselSelect = (index: number) => {
    if (
      product_id &&
      chosen_product_variant_color_?.id &&
      index !== currentImageIndex
    ) {
      setCurrentImageIndex(index)
    }
  }

  return (
    <div className="relative">
      {validIllustrations?.length > 0 ? (
        <Carousel
          className="relative"
          setApi={(api) => {
            if (api) {
              api.on('select', () => {
                handleCarouselSelect(api.selectedScrollSnap())
              })
            }
          }}
        >
          <CarouselContent>
            {validIllustrations.map((illustration) => {
              return (
                <CarouselItem key={illustration.id}>
                  {/* <ProductImage
                    imageClassName={'h-[500px] object-contain rounded-sm'}
                    containerClassName={'rounded-md'}
                    image_url={illustration?.image_url!}
                  /> */}
                  <DesignImage
                    onImageError={() => {
                      // setImageError(true)
                      // onImageLoad?.() // Call even on error so UI doesn't get stuck
                    }}
                    onImageLoad={() => {
                      // onImageLoad?.()
                    }}
                    logo_url={chosen_logo_url || null}
                    image_url={illustration?.image_url || ''}
                    design={
                      (illustration as any)?.design?.[0] as Omit<
                        Design_,
                        'design_url' | 'image_url'
                      >
                    }
                    key={0}
                    containerClassName={'rounded-2xl'}
                    imageClassName={'object-contain max-h-[400px]'}
                  />
                </CarouselItem>
              )
            })}
          </CarouselContent>

          {validIllustrations?.length > 1 && (
            <>
              <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2" />
              <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2" />
              <CarouselIndicator className="absolute bottom-4 left-1/2 -translate-x-1/2" />
            </>
          )}
        </Carousel>
      ) : (
        <div className="h-[500px] flex items-center justify-center rounded-md bg-gray-100">
          <p className="text-sm text-foreground/60">No images available</p>
        </div>
      )}
    </div>
  )
}
